// Pixel-art de la oficina (vista cenital) — TODO se dibuja aquí, en
// rectángulos de píxel entero sobre un canvas de "píxeles de arte"
// que después se amplía por un factor ENTERO. Por eso el resultado es nítido
// de verdad (nada de PNG reescalado con interpolación, nada de sprites
// borrosos).
//
// Regla de la casa: nada decorativo sin significado. Cada cosa que se dibuja
// o existe en la referencia de Pau (tarima, placas, mesas, sillas, alfombras,
// lámparas, plantas) o
// dice un dato (halo de estado, gesto del personaje).

/** Medidas del puesto de trabajo (mesa + silla + persona) en píxeles de arte. */
export const PUESTO = { w: 72, h: 92, mesaW: 52, mesaH: 20, sillaW: 20, sillaH: 20 };

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Gesto del personaje: lo que está haciendo. */
export type Animacion = 'teclea' | 'lee' | 'piensa' | 'duerme';

/* ------------------------------------------------------------------ *
 * Retratos — una cara distinta y estable por semilla (el agente)
 * ------------------------------------------------------------------ */

export interface RetratoPixel {
  piel: string;
  pelo: string;
  camisa: string;
  /** 0 corto · 1 recogido · 2 rizado · 3 raya · 4 calvo · 5 melena · 6 flequillo */
  peinado: number;
  gafas: boolean;
  barba: boolean;
  /** 0 estrecho · 1 medio · 2 ancho — tres siluetas de cuerpo distintas */
  cuerpo: number;
}

const PIEL = ['#E8B98E', '#D9A06B', '#C68F5F', '#A9714A', '#F0C9A4', '#8D5A3B'];
const PELO = ['#3A2F28', '#6E4632', '#1D1815', '#8A6A45', '#4E3B30', '#B08A55'];
const CAMISA = ['#3E5C8A', '#8E5B76', '#6F7A45', '#41786D', '#A2694A', '#57538A', '#9C4F55', '#4C6E96'];

function hash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

export function retratoDe(semilla: string): RetratoPixel {
  const h = hash(semilla);
  return {
    piel: PIEL[h % PIEL.length]!,
    pelo: PELO[(h >>> 5) % PELO.length]!,
    camisa: CAMISA[(h >>> 10) % CAMISA.length]!,
    peinado: (h >>> 15) % 7,
    gafas: ((h >>> 20) & 3) === 0,
    barba: ((h >>> 22) & 3) === 0,
    cuerpo: (h >>> 25) % 3,
  };
}

export type Ctx = CanvasRenderingContext2D;

/* ------------------------------------------------------------------ *
 * Paleta
 * ------------------------------------------------------------------ */

export const PAL = {
  vacio: '#0B0906',
  muro: '#241B12',
  muroLuz: '#312518',
  oro: '#F0B90B',
  oroClaro: '#F0D06A',
  tarima: ['#B98551', '#B07C4A', '#C08D57', '#A97444'],
  tarimaLinea: '#8A5C31',
  tarimaLuz: '#D3A06A',
  alfombra: '#141416',
  alfombraBorde: '#F0B90B',
  mesa: '#3A3A42',
  mesaLuz: '#5A5A63',
  mesaSombra: '#1A1A1D',
  metal: '#2C323C',
  metalLuz: '#3E4655',
  pantalla: '#12303F',
  pantallaLuz: '#F0B90B',
  papel: '#E7DFC9',
  silla: '#1E1E22',
  sillaLuz: '#34343A',
  maceta: '#2E2E33',
  hoja: '#547F3C',
  hojaLuz: '#71A551',
  lampara: '#F2D488',
  sombra: 'rgba(0,0,0,0.30)',
  tinta: '#0D0B08',
} as const;

/** Ruido determinista (no random: la oficina se ve igual en cada visita). */
function n(x: number, y: number): number {
  let h = (x * 374761393 + y * 668265263) >>> 0;
  h = (h ^ (h >>> 13)) >>> 0;
  h = Math.imul(h, 1274126177) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

const r = (ctx: Ctx, x: number, y: number, w: number, h: number, c: string) => {
  ctx.fillStyle = c;
  ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
};

/* ------------------------------------------------------------------ *
 * Suelos y arquitectura
 * ------------------------------------------------------------------ */

/** Tarima de madera cálida: tablas horizontales con juntas y vetas. */
export function tarima(ctx: Ctx, b: Rect): void {
  const alturaTabla = 9;
  for (let y = b.y; y < b.y + b.h; y += alturaTabla) {
    const alto = Math.min(alturaTabla, b.y + b.h - y);
    // las tablas se parten en trozos de largo variable, como una tarima real
    let x = b.x - (n(0, y) % 40);
    while (x < b.x + b.w) {
      const largo = 48 + (n(x, y) % 46);
      const x0 = Math.max(b.x, x);
      const x1 = Math.min(b.x + b.w, x + largo);
      if (x1 > x0) {
        r(ctx, x0, y, x1 - x0, alto, PAL.tarima[n(x, y) % PAL.tarima.length]!);
        // veta
        if ((n(x, y + 7) & 3) === 0 && alto > 3) {
          r(ctx, x0 + 4, y + 3, Math.min(18, x1 - x0 - 8), 1, PAL.tarimaLinea);
        }
        // junta vertical
        if (x1 < b.x + b.w) r(ctx, x1 - 1, y, 1, alto, PAL.tarimaLinea);
      }
      x += largo;
    }
    if (y + alto <= b.y + b.h) r(ctx, b.x, y + alto - 1, b.w, 1, PAL.tarimaLinea);
  }
}

/** Muro perimetral de una sala: friso oscuro con moldura dorada por dentro. */
export function muroSala(ctx: Ctx, b: Rect): void {
  const g = 5;
  r(ctx, b.x, b.y, b.w, g, PAL.muro);
  r(ctx, b.x, b.y + b.h - g, b.w, g, PAL.muro);
  r(ctx, b.x, b.y, g, b.h, PAL.muro);
  r(ctx, b.x + b.w - g, b.y, g, b.h, PAL.muro);
  // brillo superior del zócalo
  r(ctx, b.x, b.y, b.w, 1, PAL.muroLuz);
  // moldura dorada interior
  ctx.fillStyle = PAL.oro;
  ctx.globalAlpha = 0.55;
  ctx.fillRect(b.x + g, b.y + g, b.w - g * 2, 1);
  ctx.fillRect(b.x + g, b.y + b.h - g - 1, b.w - g * 2, 1);
  ctx.fillRect(b.x + g, b.y + g, 1, b.h - g * 2);
  ctx.fillRect(b.x + b.w - g - 1, b.y + g, 1, b.h - g * 2);
  ctx.globalAlpha = 1;
  // esquinas doradas
  for (const [cx, cy] of [
    [b.x, b.y],
    [b.x + b.w - 6, b.y],
    [b.x, b.y + b.h - 6],
    [b.x + b.w - 6, b.y + b.h - 6],
  ] as const) {
    r(ctx, cx, cy, 6, 6, PAL.muro);
    r(ctx, cx + 1, cy + 1, 4, 4, PAL.oro);
    r(ctx, cx + 2, cy + 2, 2, 2, PAL.oroClaro);
  }
}

/* ------------------------------------------------------------------ *
 * Mobiliario
 * ------------------------------------------------------------------ */

export function alfombra(ctx: Ctx, x: number, y: number, w: number, h: number, color?: string): void {
  r(ctx, x, y, w, h, PAL.alfombra);
  ctx.fillStyle = color ?? PAL.alfombraBorde;
  ctx.globalAlpha = 0.85;
  ctx.fillRect(x + 2, y + 2, w - 4, 2);
  ctx.fillRect(x + 2, y + h - 4, w - 4, 2);
  ctx.fillRect(x + 2, y + 2, 2, h - 4);
  ctx.fillRect(x + w - 4, y + 2, 2, h - 4);
  ctx.globalAlpha = 0.14;
  for (let yy = y + 8; yy < y + h - 8; yy += 8) {
    for (let xx = x + 8; xx < x + w - 8; xx += 8) ctx.fillRect(xx, yy, 3, 3);
  }
  ctx.globalAlpha = 1;
}

/** Mesa vista desde arriba: tablero, canto, monitor encendido, teclado,
 * papeles y una planta pequeña. `encendida` = su persona está trabajando. */
export function mesa(ctx: Ctx, x: number, y: number, w: number, h: number, encendida: boolean, brillo: number): void {
  r(ctx, x + 2, y + h, w - 4, 3, PAL.sombra);
  r(ctx, x, y, w, h, PAL.mesa);
  r(ctx, x, y, w, 2, PAL.mesaLuz);
  r(ctx, x, y + h - 3, w, 3, PAL.mesaSombra);
  // vetas
  for (let i = 0; i < w; i += 7) if ((n(x + i, y) & 3) === 0) r(ctx, x + i, y + 4, 5, 1, PAL.mesaSombra);

  // monitor (apoyado en el borde de arriba)
  const mw = 22;
  const mx = x + Math.round(w / 2) - Math.round(mw / 2);
  const my = y - 11;
  r(ctx, mx - 1, my - 1, mw + 2, 13, PAL.metal);
  r(ctx, mx, my, mw, 11, encendida ? PAL.pantalla : '#181C22');
  if (encendida) {
    ctx.fillStyle = PAL.pantallaLuz;
    ctx.globalAlpha = 0.55 + brillo * 0.35;
    for (let i = 0; i < 4; i += 1) ctx.fillRect(mx + 2, my + 2 + i * 2, 4 + ((n(mx + i, my + brillo * 10) % 12) + 4), 1);
    ctx.globalAlpha = 1;
    r(ctx, mx, my + 11, mw, 1, PAL.pantallaLuz);
  }
  r(ctx, mx + Math.round(mw / 2) - 2, my + 12, 4, 2, PAL.metalLuz);

  // teclado + ratón
  r(ctx, x + Math.round(w / 2) - 9, y + h - 9, 18, 5, PAL.metal);
  r(ctx, x + Math.round(w / 2) - 8, y + h - 8, 16, 1, PAL.metalLuz);
  r(ctx, x + Math.round(w / 2) + 12, y + h - 8, 3, 4, PAL.metal);
  // papeles
  r(ctx, x + 4, y + h - 10, 9, 7, PAL.papel);
  r(ctx, x + 5, y + h - 8, 7, 1, '#B9AE92');
  r(ctx, x + 5, y + h - 6, 5, 1, '#B9AE92');
  // planta de mesa
  const px = x + w - 12;
  r(ctx, px, y + h - 11, 7, 6, PAL.maceta);
  r(ctx, px + 1, y + h - 15, 5, 4, PAL.hoja);
  r(ctx, px + 2, y + h - 17, 3, 3, PAL.hojaLuz);
}

export function silla(ctx: Ctx, cx: number, y: number): void {
  const w = 30;
  const x = cx - Math.round(w / 2);
  r(ctx, x + 2, y + 18, w - 4, 3, PAL.sombra);
  r(ctx, x, y - 8, w, 26, PAL.silla); // respaldo alto: asoma por detrás de la persona
  r(ctx, x + 1, y - 7, w - 2, 3, PAL.sillaLuz);
  r(ctx, x + 2, y - 4, w - 4, 1, '#242A33');
  r(ctx, x + 2, y + 2, w - 4, 1, '#242A33');
  r(ctx, x + 3, y + 16, w - 6, 3, PAL.metal);
  r(ctx, x + Math.round(w / 2) - 1, y + 16, 2, 5, PAL.metal);
  r(ctx, x + 1, y + 19, 4, 2, PAL.metal);
  r(ctx, x + w - 5, y + 19, 4, 2, PAL.metal);
}

export function planta(ctx: Ctx, x: number, y: number, k = 1): void {
  const w = 10 * k;
  r(ctx, x + 1, y + 14 * k, w, 3, PAL.sombra);
  r(ctx, x + 1, y + 9 * k, w - 2, 7 * k, PAL.maceta);
  r(ctx, x + 1, y + 9 * k, w - 2, 1, '#D07B4E');
  r(ctx, x + 2, y + 3 * k, w - 4, 7 * k, PAL.hoja);
  r(ctx, x, y + 5 * k, 3, 4 * k, PAL.hoja);
  r(ctx, x + w - 2, y + 5 * k, 3, 4 * k, PAL.hoja);
  r(ctx, x + 3, y, w - 6, 5 * k, PAL.hojaLuz);
}

/** Lámpara de pie + charco de luz cálida (la iluminación de la referencia). */
export function lampara(ctx: Ctx, x: number, y: number): void {
  const g = ctx.createRadialGradient(x + 5, y + 6, 1, x + 5, y + 6, 34);
  g.addColorStop(0, 'rgba(242,212,136,0.30)');
  g.addColorStop(1, 'rgba(242,212,136,0)');
  ctx.fillStyle = g;
  ctx.fillRect(x - 29, y - 28, 68, 68);
  r(ctx, x + 3, y + 6, 4, 16, '#6B5A3C');
  r(ctx, x + 1, y + 21, 8, 3, '#54462C');
  r(ctx, x, y, 10, 7, PAL.lampara);
  r(ctx, x + 1, y + 1, 8, 3, '#FBEDBE');
}

/* ------------------------------------------------------------------ *
 * Personajes
 * ------------------------------------------------------------------ */

/** Halo de estado bajo la silla: el color ES el estado. */
export function halo(ctx: Ctx, cx: number, cy: number, color: string, alpha: number): void {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(1, 0.42);
  ctx.beginPath();
  ctx.arc(0, 0, 17, 0, Math.PI * 2);
  ctx.strokeStyle = color;
  ctx.globalAlpha = alpha;
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(0, 0, 15, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.globalAlpha = alpha * 0.14;
  ctx.fill();
  ctx.restore();
  ctx.globalAlpha = 1;
}

/**
 * Personaje sentado, 4 frames. El gesto dice el dato:
 *   teclea → las manos bajan y suben sobre el teclado
 *   lee    → sostiene una hoja y la cabeza se ladea
 *   piensa → burbuja de puntos y cabeza que asiente
 *   duerme → cabeza caída y hombros que respiran (la Z va en el DOM)
 */
export function persona(
  ctx: Ctx,
  cx: number,
  y: number,
  ret: RetratoPixel,
  anim: Animacion,
  frame: number,
  dePie = false,
  /** 29-10 — 1 = normal; menos, medio transparente (los que se van). Quien llama deja el alfa como estaba. */
  alfa = 1,
): void {
  const f = frame & 3;
  const dorm = anim === 'duerme';
  const respira = dorm ? (f === 1 || f === 2 ? 1 : 0) : 0;
  const pielOsc = lighten(ret.piel, -34);
  const camisaOsc = lighten(ret.camisa, -22);
  const camisaLuz = lighten(ret.camisa, 20);
  const peloLuz = lighten(ret.pelo, 26);

  // sombra suave a los pies (dos óvalos: núcleo + difuminado)
  ctx.globalAlpha = 0.28 * alfa;
  r(ctx, cx - 10, y + (dePie ? 30 : 23), 20, 4, '#000000');
  ctx.globalAlpha = 0.14 * alfa;
  r(ctx, cx - 13, y + (dePie ? 29 : 22), 26, 6, '#000000');
  ctx.globalAlpha = alfa;

  if (dePie) {
    // piernas
    r(ctx, cx - 6, y + 18, 5, 13, '#2E3440');
    r(ctx, cx + 1, y + 18, 5, 13, '#2E3440');
    r(ctx, cx - 7, y + 29, 6, 3, '#1B1F26');
    r(ctx, cx + 1, y + 29, 6, 3, '#1B1F26');
  }

  // torso — tres complexiones (hombro estrecho / medio / ancho)
  const cuerpoY = y + 11 + (dorm ? 3 : 0) + respira;
  const cuerpoH = (dePie ? 10 : 13) + (ret.cuerpo === 2 ? 1 : 0);
  const medio = 7 + ret.cuerpo * 2; // 7 · 9 · 11 a cada lado
  r(ctx, cx - medio, cuerpoY, medio * 2, cuerpoH, ret.camisa);
  r(ctx, cx - medio, cuerpoY, medio * 2, 1, camisaLuz);
  r(ctx, cx - medio, cuerpoY, 1, cuerpoH, camisaOsc);
  r(ctx, cx + medio - 1, cuerpoY, 1, cuerpoH, camisaOsc);
  if (ret.cuerpo === 2) r(ctx, cx - medio + 2, cuerpoY + 3, medio * 2 - 4, 1, camisaOsc);
  r(ctx, cx - 3, cuerpoY, 6, 3, camisaOsc); // cuello de la camisa
  r(ctx, cx - 1, cuerpoY, 2, 4, lighten(ret.camisa, -34));

  // brazos y manos (lo que se mueve al teclear)
  const dy = anim === 'teclea' ? (f === 0 || f === 2 ? 0 : f === 1 ? 1 : 2) : 0;
  r(ctx, cx - medio - 3, cuerpoY + 3, 3, 8, ret.camisa);
  r(ctx, cx + medio, cuerpoY + 3, 3, 8, ret.camisa);
  r(ctx, cx - medio - 3, cuerpoY + 10 + dy, 3, 3, ret.piel);
  r(ctx, cx + medio, cuerpoY + 10 + (anim === 'teclea' ? 2 - dy : 0), 3, 3, ret.piel);

  // hoja cuando lee
  if (anim === 'lee') {
    const inc = f === 1 || f === 2 ? 1 : 0;
    r(ctx, cx - 7, cuerpoY + 4 + inc, 14, 10, PAL.papel);
    r(ctx, cx - 7, cuerpoY + 4 + inc, 14, 1, '#FFFBF0');
    r(ctx, cx - 5, cuerpoY + 6 + inc, 10, 1, '#A79C82');
    r(ctx, cx - 5, cuerpoY + 8 + inc, 7, 1, '#A79C82');
    r(ctx, cx - 5, cuerpoY + 10 + inc, 9, 1, '#A79C82');
  }

  // ---- cabeza ----
  const cabY = y + (dorm ? 2 : 0) + respira - (dePie ? 1 : 0);
  const hx = cx - 7;
  const hw = 14;
  r(ctx, hx, cabY, hw, 12, ret.piel);          // cara
  r(ctx, hx, cabY, hw, 1, lighten(ret.piel, 18)); // luz en la frente
  r(ctx, hx, cabY + 11, hw, 1, pielOsc);        // barbilla
  r(ctx, hx - 1, cabY + 4, 1, 4, ret.piel);     // orejas
  r(ctx, hx + hw, cabY + 4, 1, 4, ret.piel);

  // pelo, con volumen
  const pelo = ret.pelo;
  switch (ret.peinado) {
    case 1: // recogido con moño
      r(ctx, hx - 1, cabY - 3, hw + 2, 5, pelo);
      r(ctx, hx - 1, cabY + 2, 2, 4, pelo);
      r(ctx, hx + hw - 1, cabY + 2, 2, 4, pelo);
      r(ctx, hx + hw - 1, cabY - 6, 5, 5, pelo);
      r(ctx, hx + hw, cabY - 5, 3, 2, peloLuz);
      break;
    case 2: // rizado
      r(ctx, hx - 1, cabY - 4, hw + 2, 6, pelo);
      r(ctx, hx - 3, cabY - 2, 3, 6, pelo);
      r(ctx, hx + hw, cabY - 2, 3, 6, pelo);
      r(ctx, hx + 1, cabY - 6, 4, 3, pelo);
      r(ctx, hx + 8, cabY - 6, 4, 3, pelo);
      r(ctx, hx + 2, cabY - 3, 4, 1, peloLuz);
      break;
    case 3: // raya al lado
      r(ctx, hx - 1, cabY - 3, hw + 2, 5, pelo);
      r(ctx, hx + 1, cabY + 1, 5, 2, pelo);
      r(ctx, hx + 2, cabY - 2, 7, 1, peloLuz);
      break;
    case 4: // entradas
      r(ctx, hx + 1, cabY - 2, hw - 2, 3, pelo);
      r(ctx, hx, cabY - 1, 2, 3, pelo);
      r(ctx, hx + hw - 2, cabY - 1, 2, 3, pelo);
      break;
    case 5: // melena larga
      r(ctx, hx - 2, cabY - 4, hw + 4, 6, pelo);
      r(ctx, hx - 3, cabY - 1, 3, 14, pelo);
      r(ctx, hx + hw, cabY - 1, 3, 14, pelo);
      r(ctx, hx - 3, cabY + 12, 4, 2, pelo);
      r(ctx, hx + hw - 1, cabY + 12, 4, 2, pelo);
      r(ctx, hx, cabY - 3, 6, 1, peloLuz);
      break;
    case 6: // flequillo recto
      r(ctx, hx - 2, cabY - 4, hw + 4, 7, pelo);
      r(ctx, hx - 2, cabY + 1, 2, 8, pelo);
      r(ctx, hx + hw, cabY + 1, 2, 8, pelo);
      r(ctx, hx + 1, cabY - 2, 8, 1, peloLuz);
      break;
    default: // corto
      r(ctx, hx - 1, cabY - 3, hw + 2, 5, pelo);
      r(ctx, hx + 2, cabY - 2, 6, 1, peloLuz);
  }

  // cejas
  if (!dorm) {
    r(ctx, hx + 2, cabY + 4, 3, 1, pelo);
    r(ctx, hx + 9, cabY + 4, 3, 1, pelo);
  }

  // ojos (cerrados si duerme)
  if (dorm) {
    r(ctx, hx + 2, cabY + 7, 3, 1, PAL.tinta);
    r(ctx, hx + 9, cabY + 7, 3, 1, PAL.tinta);
  } else {
    r(ctx, hx + 2, cabY + 6, 3, 2, '#F6F1E6');
    r(ctx, hx + 9, cabY + 6, 3, 2, '#F6F1E6');
    const mira = anim === 'piensa' && (f === 1 || f === 2) ? 1 : 0;
    r(ctx, hx + 3 + mira, cabY + 6, 1, 2, PAL.tinta);
    r(ctx, hx + 10 + mira, cabY + 6, 1, 2, PAL.tinta);
  }

  if (ret.gafas) {
    r(ctx, hx + 1, cabY + 5, 5, 4, '#20242B');
    r(ctx, hx + 8, cabY + 5, 5, 4, '#20242B');
    r(ctx, hx + 2, cabY + 6, 3, 2, '#9FD6E8');
    r(ctx, hx + 9, cabY + 6, 3, 2, '#9FD6E8');
    r(ctx, hx + 6, cabY + 6, 2, 1, '#20242B');
  }

  // nariz y boca
  r(ctx, hx + 6, cabY + 8, 2, 1, pielOsc);
  if (dorm) {
    r(ctx, hx + 6, cabY + 10, 2, 1, pielOsc);
  } else {
    r(ctx, hx + 5, cabY + 10, 4, 1, '#8A4A44');
  }

  // barba
  if (ret.barba) {
    r(ctx, hx, cabY + 8, 2, 4, pelo);
    r(ctx, hx + hw - 2, cabY + 8, 2, 4, pelo);
    r(ctx, hx + 2, cabY + 11, hw - 4, 2, pelo);
    r(ctx, hx + 4, cabY + 9, 6, 1, lighten(pelo, -12));
  }
}

export function lighten(hex: string, amt: number): string {
  const v = parseInt(hex.slice(1), 16);
  const c = (x: number) => Math.max(0, Math.min(255, x + amt));
  return `#${[c(v >> 16), c((v >> 8) & 255), c(v & 255)].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

/* ------------------------------------------------------------------ *
 * Iluminación final
 * ------------------------------------------------------------------ */

